import { useMemo } from "react";
import { useAuth } from "../../../auth";
import { parseOrgPlanName } from "./org-onboarding-helpers";
import type { OrgPlanName } from "./types";

/**
 * Reads the selected organization's plan from auth and narrows it to a known
 * {@link OrgPlanName}.
 *
 * @returns
 *   - `plan`: the narrowed {@link OrgPlanName}, or `undefined` when the raw plan
 *     type is unrecognized (callers should treat unknown plans as ungated).
 *   - `rawPlanType`: the unnarrowed `organization.plan?.type` string, for
 *     diagnostics / display.
 *   - `isLoading`: `true` while organizations are still loading.
 */
export const useOrgPlan = (): { plan: OrgPlanName | undefined; rawPlanType: string | undefined; isLoading: boolean } => {
  const { selectedOrganization, isLoadingOrganizations } = useAuth();
  const rawPlanType = selectedOrganization?.plan?.type;

  return useMemo(
    () => ({
      plan: parseOrgPlanName(rawPlanType),
      rawPlanType,
      isLoading: isLoadingOrganizations,
    }),
    [isLoadingOrganizations, rawPlanType]
  );
};
